import { jwtDecode } from 'jwt-decode';
import { setToken, addUser, deleteUser } from './userSlice';

// Thunk for user login and fetching user profile
export const loginAndFetchUser = ({ email, password }) => async (dispatch) => {
    try {
        // Clear any existing session first
        dispatch(deleteUser());

        const current_date = new Date().toISOString().slice(0, 10);
        const current_time = new Date().toLocaleTimeString('en-GB', { hour12: false });
        const formData = { email, password, current_date, current_time };

        const response = await fetch(`${process.env.REACT_APP_API_URL}/users/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(formData),
        });

        const result = await response.json();

        if (result.status === 200) {
            const token = result.data.token;

            // Set token in Redux (will sync to localStorage automatically)
            dispatch(setToken(token));

            // Fetch user profile after login
            const userId = result.data.user_id || result.data.id || result.data.user?.id;

            if (userId) {
                const userResp = await fetch(`${process.env.REACT_APP_API_URL}/users/${userId}`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                });
                const userJson = await userResp.json();
                const userData = userJson?.data?.data?.[0] || userJson?.data || userJson;
                dispatch(addUser(userData));
                return { user: userData, token: token, success: true };
            } else {
                // If no userId, use data from login response
                dispatch(addUser(result.data));
                return { user: result.data, token: token, success: true };
            }
        } else {
            throw new Error(result.error || result.message || 'Login failed');
        }
    } catch (error) {
        // Clear any partial auth state on error
        dispatch(deleteUser());
        throw new Error(error.message || 'Network error');
    }
};

/**
 * Restore the user profile after a hard refresh.
 *
 * On hard refresh, `initializeAuth` rehydrates token + isAuthenticated from
 * localStorage but `currentUser` is reset to null (it's not persisted). Without
 * `currentUser.role`, RequireAuth's role check fails for every protected route
 * and the user lands in a blank/redirect-loop state (TASK-25May-07 — Aerial
 * hard-refresh blank page).
 *
 * This thunk decodes the JWT, extracts user_id, and fetches the user profile
 * from /users/{userId} to repopulate currentUser. Idempotent — returns early
 * if currentUser is already loaded.
 *
 * Failure modes:
 *   - No token in localStorage → no-op (RequireAuth will redirect to login)
 *   - JWT undecodable → clear auth state, force re-login
 *   - 401/403 from server → token invalid server-side, clear auth, force re-login
 *   - Other network errors → leave state alone (next hard refresh retries)
 */
export const restoreUserSession = () => async (dispatch, getState) => {
    // Already loaded — nothing to do.
    if (getState().user?.currentUser) return;

    const token = localStorage.getItem('token');
    if (!token) return;

    let userId;
    try {
        const decoded = jwtDecode(token);
        userId = decoded.user_id || decoded.id || decoded.sub;
    } catch (e) {
        console.warn('[restoreUserSession] JWT decode failed:', e?.message);
        dispatch(deleteUser());
        return;
    }
    if (!userId) {
        console.warn('[restoreUserSession] JWT has no user_id claim');
        dispatch(deleteUser());
        return;
    }

    try {
        const resp = await fetch(`${process.env.REACT_APP_API_URL}/users/${userId}`, {
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });
        if (!resp.ok) {
            if (resp.status === 401 || resp.status === 403) {
                // Token rejected server-side — force re-login.
                dispatch(deleteUser());
            }
            // Other status codes: leave state alone, RequireAuth will keep
            // showing Loading and the next refresh will retry.
            return;
        }
        const json = await resp.json();
        // Match the shape-tolerance from loginAndFetchUser EXACTLY:
        //   { data: { data: [user] } } → preferred
        //   { data: user }              → fallback
        //   user                         → fallback
        // Dispatch addUser unconditionally — same as login flow. The previous
        // `if (userData?.role !== undefined)` guard was over-defensive and
        // caused the dispatch to be skipped when the response wrapped role
        // differently than expected, leaving currentUser null and the
        // RequireAuth Loading guard hanging forever (Vijay's 26 May report).
        const userData = json?.data?.data?.[0] || json?.data || json;
        if (userData && typeof userData === 'object') {
            // Visible in the browser console so future "Loading… forever"
            // bugs can be debugged with one F12 instead of a git-bisect.
            console.info('[restoreUserSession] addUser dispatched, role=', userData?.role);
            dispatch(addUser(userData));
        } else {
            console.warn('[restoreUserSession] /users/:id returned non-object payload:', userData);
        }
    } catch (e) {
        console.warn('[restoreUserSession] fetch failed:', e?.message);
        // Network error — don't force logout. User can retry on next page load.
    }
};
